'use client';
import { useAddOrUpdateCampaign } from '@/actions/feed.actions';
import getCampaignText from '@/components/Campaign/campaignTexts.constant';
import PostViewersSelectorSchema from '@/components/PostViewerSelector/postViewerSelector.validator';
import PostViewersSelectorNekiAdminSchema from '@/components/PostViewerSelector/postViewerSelectorNekiAdmin.validator';
import Button from '@/components/next-ui/Button';
import Dialog from '@/components/next-ui/Dialog';
import { CampaignType } from '@/constants/postType.constants';
import {
  useIsCommunityAdmin,
  useIsNonProfitAdmin,
  useIsNonProfitUser,
} from '@/hooks/useUser';
import {
  fetchUserNonProfit,
  resetNonProfitFilters,
} from '@/stores/nonProfitActions';
import { hasAppAccess } from '@/stores/userActions';
import { AppPreferenceItem } from '@/utils/appPreference';
import { useQuery } from '@tanstack/react-query';
import { Tab, TabPanel, TabPanels, Tabs, TabsList } from '@ui/Tab';
import { cx } from 'cva';
import { isEmpty, size } from 'lodash-es';
import React from 'react';
import { IoMdVideocam } from 'react-icons/io';
import { MdPhoto } from 'react-icons/md';
import * as yup from 'yup';
import AttachPostMedia from '../CreatePostDialog/AttachPostMedia';
import ImageCarousel from '../ImageCarousel';
import CreateCampaignForm from './CreateCampaignForm';
import ActivitySuccessDialog from '@/components/ActivitySuccessDialog';
import useUserProfile from '@/hooks/useUserProfile';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { add } from 'date-fns';
import { PublishToPublicPage } from '@/components/PublishToPublicPage';
import { ProfileTypes, TargetViewers } from '@/constants/profileType.constant';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import useStore from '@/stores/store';
import Typography from '@/components/next-ui/Typography';

let schema = yup.object().shape({
  campaignType: yup
    .string()
    .oneOf([CampaignType.FundRaise, CampaignType.CollectGoods])
    .required(),
  fundRaiseType: yup
    .string()
    .when('campaignType', (campaignType, schemaRef) => {
      if (campaignType === CampaignType.FundRaise) {
        return schemaRef
          .required('Please enter a fund raise type')
          .oneOf(
            [CampaignType.FundRaise, CampaignType.P2PFundRaise],
            'Invalid fund raise type',
          );
      }
      return schemaRef.nullable().notRequired();
    }),
  title: yup
    .string()
    .required('Please enter a name')
    .max(110, 'Name is too long'),
  description: yup.string().required('Please enter a description'),
  startDate: yup
    .date()
    .required('You must enter a start date')
    .typeError('You must enter a valid date'),
  endDate: yup
    .date()
    .required('You must enter an end date')
    .typeError('You must enter a valid date')
    .min(yup.ref('startDate'), 'End date must be after or equal to start date'),
  nonProfit: yup
    .object()
    .shape({ id: yup.number() })
    .required('Please select a non-profit')
    .typeError('Please select a non-profit'),
  project: yup
    .object()
    .shape({ id: yup.string() })
    .when('campaignType', (campaignType, schemaRef) => {
      if (campaignType === CampaignType.CollectGoods) {
        return schemaRef
          .required('Please select a project')
          .typeError('Please select a project');
      }
      return schemaRef.nullable().notRequired();
    }),
  address: yup.string().when('campaignType', (campainType, schemaRef) => {
    if (campainType === CampaignType.CollectGoods) {
      return schemaRef.required('Please enter an address');
    }
    return schemaRef.nullable().notRequired();
  }),
  goalAmount: yup.number().when('campaignType', (campainType, schemaRef) => {
    if (campainType === CampaignType.FundRaise) {
      return schemaRef
        .required('Please enter a goal amount')
        .min(1, 'Goal amount must be greater than 0')
        .typeError('You must enter a valid number');
    }
    return schemaRef.nullable().notRequired();
  }),
});
// .concat(PostViewersSelectorSchema);

function CreateCampaignDialogTemplate(props) {
  const {
    open,
    post = null,
    type = CampaignType.FundRaise,
    onClose,
    updateHeader,
    handleBack,
  } = props;
  const isEditMode = !!post;

  const profile = useUserProfile();
  const userProfile = useStore((state) => state.user);

  const isNekiAdmin = userProfile?.isNekiAdmin;
  const isNonProfitUser = useIsNonProfitUser();
  const isNonProfitAdmin = useIsNonProfitAdmin();
  const isAdmin = useIsCommunityAdmin() || isNekiAdmin;

  // schema = schema.concat(
  //   isNekiAdmin
  //     ? PostViewersSelectorNekiAdminSchema
  //     : PostViewersSelectorSchema,
  // );

  schema = schema.concat(PostViewersSelectorSchema);

  const [defaultTabIndex, setDefaultTabIndex] = React.useState(0);
  const [showFundRaiserTab, setShowFundRaiserTab] = React.useState(false);
  const [showGoodsTab, setShowGoodsTab] = React.useState(false);
  const [showP2PFundRaiserTab, setShowP2PFundRaiserTab] = React.useState(false);
  const [reward, setReward] = React.useState(null);
  const [createdPost, setCreatedPost] = React.useState(null);
  const [submitted, setSubmitted] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [step, setStep] = React.useState('form');
  const [selectedImages, setSelectedImages] = React.useState(null);
  const [showNoImagesError, setShowNoImagesError] = React.useState(false);
  const [selectedVideos, setSelectedVideos] = React.useState(null);
  const [tabType, setTabType] = React.useState(type);

  const isLessThanSmallScreen = useBreakpointHooks((bp) => bp.useSmaller('sm'));

  const getDefaultFormValues = (npo, campaignType, campaignPost) => {
    const defaultFormValues = {
      postAs: isNonProfitAdmin ? 'org' : 'me',
      targetViewers: isNonProfitAdmin ? 'all' : 'my-org',
      targetOrgs: [],
      isFeatured: false,
      campaignType: campaignType,
      nonProfit: npo || null,
      project: null,
      title: '',
      description: '',
      address: '',
      goalAmount: null,
      startDate: new Date(),
      endDate: add(new Date(), { days: 90 }),
      fundRaiseType: 'None',
    };

    if (isEditMode && campaignPost) {
      const campaign = campaignPost.sourcePostData;
      defaultFormValues.postAs = campaignPost.postAs;
      defaultFormValues.targetOrgs = campaignPost.targetOrgs;
      defaultFormValues.targetViewers = campaignPost.targetViewers;
      defaultFormValues.campaignType = campaign.campaignType;
      defaultFormValues.project = campaign.projectId || null;
      defaultFormValues.title = campaign.title || '';
      defaultFormValues.description = campaign.message || '';
      defaultFormValues.address = campaign.address || '';
      defaultFormValues.goalAmount = campaign?.goalAmount || null;
      defaultFormValues.startDate = new Date(campaign.startDate);
      defaultFormValues.endDate = new Date(campaign.endDate);
      defaultFormValues.videoUrl = campaign.videoUrl || '';
      defaultFormValues.fundRaiseType = campaignPost.fundRaiseType || 'None';
    }

    if (isNekiAdmin) {
      defaultFormValues.nekiAdminAuthor = null;
      defaultFormValues.nekiAdminOrg = null;
      defaultFormValues.postOrg = null;
    }

    return defaultFormValues;
  };

  const isP2PFundraiser = tabType === CampaignType.P2PFundRaise;

  const formMethods = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: getDefaultFormValues(null, tabType, post),
  });

  const { data: userNonProfit } = useQuery({
    queryKey: ['create-campaign-nonprofit-org'],
    queryFn: async () => {
      const npo = await fetchUserNonProfit(true);
      formMethods.setValue('nonProfit', npo);
      return npo;
    },
    enabled: isNonProfitUser || isNonProfitAdmin || isNekiAdmin,
    staleTime: Infinity,
    refetchInterval: Infinity,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const {
    mutateAsync: addOrUpdateCampaign,
    isSuccess,
    isError,
  } = useAddOrUpdateCampaign();

  React.useEffect(() => {
    if (submitted) {
      if (isError) {
        setSubmitted(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted, isSuccess, isError]);

  React.useEffect(() => {
    if (open) {
      if (isEditMode) {
        const campaign = post.sourcePostData;
        if (campaign.campaignType === CampaignType.FundRaise) {
          if (campaign.fundRaiseType === CampaignType.P2PFundRaise) {
            setShowP2PFundRaiserTab(true);
            setShowFundRaiserTab(false);
          } else {
            setShowP2PFundRaiserTab(false);
            setShowFundRaiserTab(true);
          }
          setShowGoodsTab(false);
          updateSelectedTab(CampaignType.FundRaise);
        } else {
          setShowGoodsTab(true);
          setShowFundRaiserTab(false);
          setShowP2PFundRaiserTab(false);
          updateSelectedTab(CampaignType.CollectGoods);
        }
        setDefaultTabIndex(0);
      } else {
        // const showFundRaise = hasAppAccess(AppPreferenceItem.FundRaise);
        // const showInKind = hasAppAccess(AppPreferenceItem.InKindDonation);
        // const showP2PFundRaise = hasAppAccess(AppPreferenceItem.P2PFundRaise);
        // setShowFundRaiserTab(showFundRaise);
        // setShowGoodsTab(showInKind);
        // setShowP2PFundRaiserTab(showP2PFundRaise);
        if (type === CampaignType.FundRaise) {
          setShowFundRaiserTab(true);
        } else if (type === CampaignType.CollectGoods) {
          setShowGoodsTab(true);
        } else if (type === CampaignType.P2PFundRaise) {
          setShowP2PFundRaiserTab(true);
        }
        let tab = type;
        // if (!type) {
        //   if (showFundRaiserTab) {
        //     tab = CampaignType.FundRaise;
        //   } else if (showInKind) {
        //     tab = CampaignType.CollectGoods;
        //   } else {
        //     tab = CampaignType.P2PFundRaise;
        //   }
        // }

        updateSelectedTab(tab);
        setDefaultTabIndex(0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type]);

  React.useEffect(() => {
    getTitle();
  }, [step]);

  const getButtonLabel = () => {
    if (isEditMode) {
      return 'Save';
    }
    return isP2PFundraiser || isAdmin ? 'Next' : 'Post';
  };

  const updateSelectedTab = (tab) => {
    setTabType(tab);
    const campaignType =
      tab === CampaignType.P2PFundRaise ? CampaignType.FundRaise : tab;
    formMethods.reset(getDefaultFormValues(userNonProfit, campaignType, post));
  };

  const handleTabChange = (val) => {
    setSelectedImages(null);
    setSelectedVideos(null);

    let numTabs =
      Number(showFundRaiserTab) +
      Number(showGoodsTab) +
      Number(showP2PFundRaiserTab);

    if (val === 0) {
      if (showFundRaiserTab) {
        updateSelectedTab(CampaignType.FundRaise);
      } else if (showGoodsTab) {
        updateSelectedTab(CampaignType.CollectGoods);
      } else if (showP2PFundRaiserTab) {
        updateSelectedTab(CampaignType.P2PFundRaise);
      }
    } else if (val === 1) {
      if (numTabs === 3) {
        if (showGoodsTab) {
          updateSelectedTab(CampaignType.CollectGoods);
        }
      } else {
        if (showP2PFundRaiserTab) {
          updateSelectedTab(CampaignType.P2PFundRaise);
        } else if (showGoodsTab) {
          updateSelectedTab(CampaignType.CollectGoods);
        } else if (showFundRaiserTab) {
          updateSelectedTab(CampaignType.FundRaise);
        }
      }
    } else if (val === 2 && showP2PFundRaiserTab) {
      updateSelectedTab(CampaignType.P2PFundRaise);
    }

    setDefaultTabIndex(val);
  };

  const handleClose = () => {
    setStep('form');
    resetNonProfitFilters();
    setSubmitted(false);
    setSelectedImages(null);
    setShowNoImagesError(false);
    setSelectedVideos(null);
    formMethods.reset(getDefaultFormValues(userNonProfit, tabType, null));
    onClose();
  };

  const handleCreateCampaign = async (formValues) => {
    const payload = {
      ...formValues,
      postAs:
        formValues.fundRaiseType === CampaignType.P2PFundRaise
          ? ProfileTypes.Individual
          : formValues.postAs,
      targetViewers:
        formValues.fundRaiseType === CampaignType.P2PFundRaise
          ? TargetViewers.All
          : formValues.targetViewers,
    };

    // TODO: check
    if (!isEmpty(payload.targetOrgs)) {
      payload.targetViewers = TargetViewers.SelectedOrg;
    }

    if (!submitted) {
      setSaving(true);
      setSubmitted(true);
      const data = await addOrUpdateCampaign({
        data: payload,
        images: selectedImages,
        videos: selectedVideos,
        post,
      });
      setStep('success');
      setReward(data?.reward);
      setCreatedPost(data?.post);
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (formMethods.formState.isValid) {
      handleFormSubmit(formMethods.getValues());
    } else {
      formMethods.trigger();

      setTimeout(() => {
        if (formMethods.formState.isValid) {
          handleFormSubmit(formMethods.getValues());
        }
      }, 50);
    }
  };

  const handleFormSubmit = async (formValues) => {
    if (step === 'form') {
      if (formMethods.formState.isValid) {
        const hasImages = formValues.project
          ? true
          : !isEmpty(selectedImages) || !isEmpty(selectedVideos);
        if (hasImages) {
          if (isP2PFundraiser || isAdmin) {
            setStep('publish');
          } else {
            handleCreateCampaign(formValues);
          }
        } else {
          setShowNoImagesError(true);
        }
      }
    } else {
      // spread formValues after formData to override default values else default values are taken from formData
      const payload = {
        ...formValues,
      };

      handleCreateCampaign(payload);
    }
  };

  const handlePublish = async () => {
    handleCreateCampaign(formMethods.getValues());
  };

  const updatePostImages = (images) => {
    setSelectedImages(images || []);
    setSelectedVideos([]);
    setStep('form');
    setShowNoImagesError(false);
  };

  const updatePostVideos = (videos) => {
    setSelectedVideos(videos || []);
    setSelectedImages([]);
    setStep('form');
    setShowNoImagesError(false);
  };

  const getCampaignTitle = () => {
    switch (tabType) {
      case CampaignType.FundRaise:
        return 'Fundraiser';
      case CampaignType.CollectGoods:
        return 'In-Kind Fundraiser';
      case CampaignType.P2PFundRaise:
        // return 'Peer-to-Peer Fundraiser';
        return 'a Fundraiser and share with Friends';
      default:
        return '';
    }
  };

  const getTitle = () => {
    const campaignTitle = getCampaignTitle();
    let title = '';
    switch (step) {
      case 'pictures':
        title = 'Add Pictures';
        break;
      case 'videos':
        title = 'Add Videos';
        break;
      case 'success':
        title = '';
        break;
      default:
        title = isEditMode
          ? `Edit ${campaignTitle}`
          : `Create ${campaignTitle}`;
        break;
    }

    updateHeader(title);
  };

  if (!open) {
    return null;
  }

  return (
    <>
      {step === 'form' && (
        <div
          className={`flex w-full flex-col items-start justify-start overflow-y-auto pr-4`}>
          {showFundRaiserTab && (
            <div className="mt-4 flex flex-col items-start justify-start">
              {getCampaignText('fundraiserFormHeader')}
              <FormProvider {...formMethods}>
                <form
                  id={`create-campaign-form-${CampaignType.FundRaise}`}
                  onSubmit={formMethods.handleSubmit((data) =>
                    handleFormSubmit(data),
                  )}
                  className="h-full w-full">
                  <CreateCampaignForm
                    isEditMode={isEditMode}
                    type={CampaignType.FundRaise}
                    fundRaiseType={CampaignType.FundRaise}
                    images={selectedImages}
                    videos={selectedVideos}
                    isNekiAdmin={isNekiAdmin}
                  />
                </form>
              </FormProvider>
            </div>
          )}
          {showGoodsTab && (
            <div className="mt-4 flex flex-col items-start justify-start">
              {getCampaignText('inKindFundraiserFormHeader')}
              <FormProvider {...formMethods}>
                <form
                  id={`create-campaign-form-${CampaignType.CollectGoods}`}
                  onSubmit={formMethods.handleSubmit((data) =>
                    handleFormSubmit(data),
                  )}
                  className="h-full w-full">
                  <CreateCampaignForm
                    isEditMode={isEditMode}
                    type={CampaignType.CollectGoods}
                    images={selectedImages}
                    videos={selectedVideos}
                    isNekiAdmin={isNekiAdmin}
                  />
                </form>
              </FormProvider>
            </div>
          )}
          {showP2PFundRaiserTab && (
            <div className="mt-4 flex flex-col items-start justify-start">
              {getCampaignText('p2pFundraiserFormHeader')}
              <FormProvider {...formMethods}>
                <form
                  id={`create-campaign-form-${CampaignType.P2PFundRaise}`}
                  onSubmit={formMethods.handleSubmit((data) =>
                    handleFormSubmit(data),
                  )}
                  className="h-full w-full">
                  <CreateCampaignForm
                    isEditMode={isEditMode}
                    type={CampaignType.FundRaise}
                    fundRaiseType={CampaignType.P2PFundRaise}
                    isP2PFundraiser={isP2PFundraiser}
                    images={selectedImages}
                    videos={selectedVideos}
                    isNekiAdmin={isNekiAdmin}
                  />
                </form>
              </FormProvider>
            </div>
          )}

          {size(selectedImages) > 0 && (
            <div className="mt-6 flex w-full items-center justify-start gap-4">
              <ImageCarousel
                size="xs"
                images={selectedImages}
                onChange={updatePostImages}
                disabled={isEditMode}
              />
            </div>
          )}
          {!isEmpty(selectedVideos) && (
            <div className="mt-6 flex w-full items-center justify-start gap-4">
              <ImageCarousel
                type="video"
                images={selectedVideos}
                onChange={updatePostVideos}
                disabled={isEditMode}
              />
            </div>
          )}
        </div>
      )}

      {step === 'pictures' && (
        <AttachPostMedia
          type="image"
          onChange={updatePostImages}
          assets={selectedImages || []}
        />
      )}

      {step === 'videos' && (
        <AttachPostMedia
          type="video"
          onChange={updatePostVideos}
          assets={selectedVideos || []}
        />
      )}

      {step === 'form' && (
        <div
          className={`flex w-full items-center justify-start gap-4 pt-4 ${
            isLessThanSmallScreen ? 'mb-32' : ''
          }`}>
          {!isEditMode && (
            <>
              <Button
                variant="icon"
                className="bg-body-background"
                onClick={() => setStep('pictures')}>
                <MdPhoto
                  className={cx(
                    'h-6 w-6 ',
                    showNoImagesError
                      ? 'animate-pulse text-error'
                      : 'text-text-disabled',
                  )}
                />
              </Button>

              <Button
                variant="icon"
                className="bg-body-background"
                onClick={() => setStep('videos')}>
                <IoMdVideocam className="h-6 w-6 text-text-disabled" />
              </Button>

              {showNoImagesError && (
                <Typography color="error">Please upload an image</Typography>
              )}
            </>
          )}

          <div className="ml-auto flex gap-4">
            <Button
              color="primary"
              variant="outlined"
              size="md"
              onClick={handleBack}>
              Back
            </Button>

            <Button
              color="primary"
              variant="filled"
              size="md"
              loading={saving}
              onClick={handleNext}>
              {getButtonLabel()}
            </Button>
          </div>
        </div>
      )}

      {step === 'publish' && (
        <div
          className={`flex w-full flex-1 flex-col gap-4 ${
            isLessThanSmallScreen ? 'mb-32' : ''
          }`}>
          <div className="flex-1">
            <FormProvider {...formMethods}>
              <PublishToPublicPage
                isAdmin={isAdmin}
                isNekiAdmin={isNekiAdmin}
                isP2PFundraiser={isP2PFundraiser}
              />
            </FormProvider>
          </div>

          <div className="ml-auto">
            <Button
              color="primary"
              variant="filled"
              size="md"
              loading={saving}
              onClick={handlePublish}>
              Post
            </Button>
          </div>
        </div>
      )}
      {step === 'success' && (
        <ActivitySuccessDialog
          isPost
          user={profile}
          reward={reward}
          post={createdPost}
          onClose={handleClose}
          isPostPublic={createdPost?.targetViewers === TargetViewers.All}
        />
      )}
    </>
  );
}

export default CreateCampaignDialogTemplate;
